import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { useCreatePlaygroundTextTranslation } from '@/api/generated';
import Header from '@/components/header';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useStateSearchParam } from '@/hooks/use-state-search-param';
import InputCard from '@/routes/(app)/(playground)/(text-translation)/components/input-card';
import OutputCard from '@/routes/(app)/(playground)/(text-translation)/components/output-card';
import ParametersCard from '@/routes/(app)/(playground)/(text-translation)/components/parameters-card';
import {
  type CreateTextTranslationWithTextSchema,
  createTextTranslationWithTextSchema,
} from '@/routes/(app)/(playground)/(text-translation)/lib/schema';

const TextTranslation = () => {
  const { t } = useTranslation();
  const [clusterIds, setClusterIds] = useStateSearchParam<Array<string>>({
    parameter: 'clusterIds',
  });

  const { mutate } = useCreatePlaygroundTextTranslation();

  const form = useForm<CreateTextTranslationWithTextSchema>({
    defaultValues: {
      models: [],
      sourceLanguageCode: '',
      targetLanguageCode: '',
    },
    resolver: zodResolver(createTextTranslationWithTextSchema),
  });

  const handleSubmit = useCallback(
    (values: CreateTextTranslationWithTextSchema) => {
      mutate(
        {
          data: {
            models: values.models,
            input: values.input,
            reference: values.reference,
            sourceLanguageCode: values.sourceLanguageCode,
            targetLanguageCode: values.targetLanguageCode,
            temperature: values.temperature[0] ?? 0,
            instructions: values.instructions,
          },
        },
        {
          onSuccess: (data) => {
            toast.success(t('clusters.createModelRun.success'));
            setClusterIds([
              ...(clusterIds ?? []),
              ...new Set(data.data.map((run) => run.cluster.id)),
            ]);
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [clusterIds, mutate, setClusterIds, t]
  );

  return (
    <>
      <Header
        title={t(`playground.textTranslation.title`)}
        description={t(`playground.textTranslation.description`)}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className='grid grid-cols-2 grid-rows-[auto_auto_auto] gap-8 [&>[data-card]]:max-h-[500px]'>
          <ParametersCard className='col-span-2 row-start-1' />
          <InputCard className='col-start-1 row-start-2' />
          <OutputCard
            className='col-start-2 row-start-2'
            clusterIds={clusterIds ?? []}
          />
          <Button
            type='submit'
            className='row-start-3 w-fit'
            disabled={!form.formState.isValid}>
            {t('playground.textTranslation.submit')}
          </Button>
        </form>
      </Form>
    </>
  );
};

export default TextTranslation;
